import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError } from '@/helpers/display-toast';
import { activateAccount, ActivateAccountInput } from '@/lib/auth';
import { CreatePassword } from '@/modules/authorization/create-password/components/create-password';
import { useRootActorRef, useRootSelector } from '@/router/state/root/util/context/helpers';

interface ActivateAccountFormProps {
  email: string;
  tempPassword: string;
  isMember: boolean;
}

export const ActivateAccountForm: React.FC<ActivateAccountFormProps> = ({
  email,
  tempPassword,
  isMember,
}) => {
  const { t } = useTranslation(['unauthenticated.accountActivation', 'unauthenticated.common']);
  const actorRef = useRootActorRef();
  const isRootStateLoading = useRootSelector(state => state.context.isLoading);

  const activateAccountMutation = useMutation({
    mutationFn: async (input: ActivateAccountInput) => {
      return await activateAccount(input);
    },
    onSuccess: user => {
      actorRef.send({ type: 'LOGGED_IN', user });
    },
    onError: (error: Error) => {
      const unknownError = t('error.unknown');
      const message = error?.message || unknownError;
      displayToastError(message);
    },
  });

  const header = isMember ? t('memberHeader') : t('header');
  const subtitle = isMember ? t('memberSubtitle') : t('subtitle');

  return (
    <CreatePassword
      email={email}
      header={header}
      subtitle={subtitle}
      isLoading={activateAccountMutation.isPending || isRootStateLoading}
      passwordLabel={t('createPasswordLabel')}
      passwordPlaceholder={t('createPasswordPlaceholder')}
      ctaLabel={t('continue')}
      onSubmit={({ password }) =>
        activateAccountMutation.mutate({ email, tempPassword, newPassword: password })
      }
    />
  );
};
